import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { TransactionGroup } from '../../types';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingRight: theme.spacing(1),
    position: 'relative',
  },
  operationChip: {
    fontFamily: 'monospace',
    marginLeft: theme.spacing(1),
  },
  metricCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: '0.75rem',
  },
  methodChip: {
    backgroundColor: theme.palette.grey[200],
    marginLeft: theme.spacing(1),
  },
  projectInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  performanceIndicator: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  slowIndicator: {
    color: theme.palette.error.main,
  },
  mediumIndicator: {
    color: theme.palette.warning.main,
  },
  fastIndicator: {
    color: theme.palette.success.main,
  },
}));

type PerformanceDetailComponentProps = {
  data: TransactionGroup | undefined;
  isLoading: boolean;
  error: Error | undefined;
  open: boolean;
  onClose: () => void;
};

export const PerformanceDetailComponent = ({
  data,
  isLoading,
  error,
  open,
  onClose,
}: PerformanceDetailComponentProps) => {
  const classes = useStyles();

  // Helper function to format duration
  const formatDuration = (ms: number): string => {
    if (ms === 0) return '0 ms';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
  };

  // Helper function to get performance indicator
  const getPerformanceIndicator = (avgDuration: number) => {
    if (avgDuration > 1000) {
      return {
        label: 'Slow',
        className: classes.slowIndicator,
        description: 'This transaction is performing slowly (>1s)',
      };
    }
    if (avgDuration > 500) {
      return {
        label: 'Medium',
        className: classes.mediumIndicator,
        description: 'This transaction has medium performance (500ms-1s)',
      };
    }
    return {
      label: 'Fast',
      className: classes.fastIndicator,
      description: 'This transaction is performing well (≤500ms)',
    };
  };

  // Helper function to format large numbers
  const formatCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Typography variant="h6" style={{ marginRight: 8 }}>
                {data ? data.transaction : 'Transaction Details'}
              </Typography>
              {data?.op && (
                <Chip
                  label={data.op}
                  size="small"
                  className={classes.operationChip}
                  variant="outlined"
                />
              )}
              {data?.method && (
                <Chip
                  label={data.method}
                  size="small"
                  className={classes.methodChip}
                />
              )}
            </Box>
            {data && (
              <Box className={classes.performanceIndicator}>
              <Typography 
                variant="body2" 
                className={getPerformanceIndicator(data.avgDuration).className}
              >
                {getPerformanceIndicator(data.avgDuration).label}
              </Typography>
            </Box>
            )}
          </Box>
          <IconButton onClick={onClose} size="medium">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}
        
        {data && (
          <Box>
            {/* Performance Metrics */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className={classes.metricCard} variant="outlined">
                  <CardContent>
                    <Typography className={classes.metricValue}>
                      {formatDuration(data.avgDuration)}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Average Duration
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="textSecondary"
                      style={{ display: 'block', marginTop: 4 }}
                    >
                      {getPerformanceIndicator(data.avgDuration).description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className={classes.metricCard} variant="outlined">
                  <CardContent>
                    <Typography className={classes.metricValue}>
                      {formatCount(data.transactionCount)}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Transaction Count
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="textSecondary"
                      style={{ display: 'block', marginTop: 8 }}
                    >
                      Total transactions recorded
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Information */}
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                Transaction Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Transaction Name:</strong> {data.transaction}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Operation:</strong> {data.op}
                  </Typography>
                </Grid>
                {data.method && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>HTTP Method:</strong> {data.method}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Project ID:</strong> {data.project}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Transaction ID:</strong> {data.id}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Performance Insights */}
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                Performance Insights
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" paragraph>
                        <strong>Average Response Time:</strong> {formatDuration(data.avgDuration)}
                      </Typography>
                      <Typography variant="body2" paragraph>
                        <strong>Total Requests:</strong> {data.transactionCount.toLocaleString()} transactions
                      </Typography>
                      {data.avgDuration > 0 && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>Throughput:</strong> This transaction has been called{' '}
                          {data.transactionCount.toLocaleString()} times with an average response time of{' '}
                          {formatDuration(data.avgDuration)}.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};